import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace, useStorage } from '../hooks/useApi';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  ArrowLeft, Library, Search, Download, Eye, Moon, Sun, 
  Loader2, FolderPlus, ChevronRight
} from 'lucide-react';
import * as XLSX from 'xlsx';

// GitHub repository configuration
const LIBRARY_CATALOG_URL = 'https://raw.githubusercontent.com/GitwithitDan/minddeck-library/main/catalog.json';

// Fallback/demo catalog for development
const DEMO_CATALOG = {
  version: 1,
  updated_at: new Date().toISOString(),
  categories: [
    {
      id: 'languages',
      name: 'Languages',
      description: 'Language learning decks',
      icon: '🌍'
    },
    {
      id: 'science',
      name: 'Science',
      description: 'Scientific knowledge and concepts',
      icon: '🔬'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Career and professional development',
      icon: '💼'
    }
  ],
  decks: [
    {
      id: 'demo-spanish-basics',
      name: 'Spanish Basics',
      description: 'Essential Spanish vocabulary for beginners',
      category: 'languages',
      card_count: 50,
      author: 'MindDeck Team',
      tags: ['spanish', 'beginner', 'vocabulary'],
      url: null, // Will be a GitHub raw URL
      format: 'json',
      preview: [
        { title: 'Hola', fields: { english: 'Hello' } },
        { title: 'Gracias', fields: { english: 'Thank you' } },
        { title: 'Por favor', fields: { english: 'Please' } }
      ]
    },
    {
      id: 'demo-periodic-table',
      name: 'Periodic Table Elements',
      description: 'All 118 elements with properties',
      category: 'science',
      card_count: 118,
      author: 'MindDeck Team',
      tags: ['chemistry', 'elements', 'science'],
      url: null,
      format: 'json',
      preview: [
        { title: 'Hydrogen', fields: { symbol: 'H', atomic_number: '1', category: 'Nonmetal' } },
        { title: 'Helium', fields: { symbol: 'He', atomic_number: '2', category: 'Noble gas' } },
        { title: 'Carbon', fields: { symbol: 'C', atomic_number: '6', category: 'Nonmetal' } }
      ]
    }
  ]
};

export function LibraryPage() {
  const navigate = useNavigate();
  const storage = useStorage();
  const { theme, toggleTheme } = useTheme();
  const { currentWorkspace } = useWorkspace();
  
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [previewDeck, setPreviewDeck] = useState(null);
  const [previewCards, setPreviewCards] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [importing, setImporting] = useState(false);

  // Load catalog on mount
  useEffect(() => {
    async function loadCatalog() {
      try {
        setLoading(true);
        
        // Try to fetch from GitHub
        const response = await fetch(LIBRARY_CATALOG_URL);
        if (response.ok) {
          const data = await response.json();
          setCatalog(data);
        } else {
          // Fall back to demo catalog
          console.log('Using demo catalog (GitHub catalog not configured)');
          setCatalog(DEMO_CATALOG);
        }
      } catch (err) {
        console.log('Using demo catalog:', err.message);
        setCatalog(DEMO_CATALOG);
      } finally {
        setLoading(false);
      }
    }
    
    loadCatalog();
  }, []);

  // Filter decks based on search and category
  const filteredDecks = catalog?.decks?.filter(deck => {
    const matchesSearch = !search || 
      deck.name.toLowerCase().includes(search.toLowerCase()) ||
      deck.description.toLowerCase().includes(search.toLowerCase()) ||
      deck.tags?.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    const matchesCategory = !selectedCategory || deck.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  }) || [];

  // Parse CSV text to cards
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
    const cards = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"' && !inQuotes) inQuotes = true;
        else if (char === '"' && inQuotes) inQuotes = false;
        else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      if (values.length === headers.length) {
        const title = values[headers.indexOf('title')] || values[headers.indexOf('Title')] || values[0];
        const body = values[headers.indexOf('body')] || values[headers.indexOf('notes')] || '';
        const fields = {};
        
        headers.forEach((h, idx) => {
          if (!['title', 'Title', 'body', 'notes', 'Body', 'Notes'].includes(h) && values[idx]) {
            fields[h] = values[idx];
          }
        });
        
        if (title) {
          cards.push({ title, body, fields });
        }
      }
    }
    
    return cards;
  };

  // Load full deck for preview
  const handlePreview = async (deck) => {
    setPreviewDeck(deck);
    setLoadingPreview(true);
    setLoadFailed(false);
    
    try {
      if (deck.url) {
        const response = await fetch(deck.url);

        if (!response.ok) {
          throw new Error(`Deck file not found (HTTP ${response.status})`);
        }

        let cards = [];
        if (deck.format === 'csv') {
          const text = await response.text();
          cards = parseCSV(text);
        } else if (deck.format === 'xlsx' || deck.format === 'xls') {
          // Must read as arrayBuffer — do NOT call response.text() first or the body stream is consumed
          const data = new Uint8Array(await response.arrayBuffer());
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet);
          cards = rows.map(row => ({
            title: row.title || row.Title || row.name || '',
            body: row.body || row.notes || row.Body || '',
            fields: Object.fromEntries(
              Object.entries(row).filter(([k]) =>
                !['title', 'Title', 'body', 'notes', 'Body', 'Notes', 'name'].includes(k)
              )
            )
          })).filter(c => c.title);
        } else {
          // JSON format
          const text = await response.text();
          const data = JSON.parse(text);
          cards = Array.isArray(data) ? data : data.cards || [];
        }
        
        setPreviewCards(cards);
      } else {
        // Use preview data if no URL
        setPreviewCards(deck.preview || []);
      }
    } catch (err) {
      toast.error(`Failed to load deck: ${err.message}`);
      setPreviewCards([]);
      setLoadFailed(true);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Import deck to current workspace
  const handleImport = async () => {
    if (!previewDeck || previewCards.length === 0) return;
    
    setImporting(true);
    try {
      // Use the existing import preview/confirm flow
      const preview = await storage.previewImport(previewDeck.name, previewCards);
      const result = await storage.confirmImport(preview.preview_id);
      
      toast.success(`Imported "${previewDeck.name}": ${result.cards_created} new, ${result.cards_merged} merged`);
      setPreviewDeck(null);
      setPreviewCards([]);
    } catch (err) {
      toast.error(err.message || 'Failed to import deck');
    } finally {
      setImporting(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    const category = catalog?.categories?.find(c => c.id === categoryId);
    return category?.icon || '📚';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="library-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Library className="h-5 w-5" />
                <h1 className="text-xl font-bold">Library</h1>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search decks..."
              className="pl-10"
              data-testid="library-search"
            />
          </div>
          
          {/* Category Pills */}
          {catalog?.categories && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === null ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(null)}
              >
                All
              </Button>
              {catalog.categories.map(cat => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className="gap-1"
                >
                  <span>{cat.icon}</span>
                  {cat.name}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Deck Grid */}
        {!loading && !error && (
          <>
            {filteredDecks.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center text-muted-foreground">
                  No decks found. Try a different search or category.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredDecks.map(deck => (
                  <Card 
                    key={deck.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handlePreview(deck)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{getCategoryIcon(deck.category)}</span>
                          <div>
                            <CardTitle className="text-lg">{deck.name}</CardTitle>
                            <CardDescription className="text-xs">
                              by {deck.author} • {deck.card_count} cards
                            </CardDescription>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">{deck.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {deck.tags?.slice(0, 4).map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Preview Dialog */}
      <Dialog open={!!previewDeck} onOpenChange={() => { setPreviewDeck(null); setPreviewCards([]); setLoadFailed(false); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{previewDeck && getCategoryIcon(previewDeck.category)}</span>
              {previewDeck?.name}
            </DialogTitle>
            <DialogDescription>
              {previewDeck?.description}
              <br />
              <span className="text-xs">by {previewDeck?.author} • {previewDeck?.card_count} cards</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 min-h-0">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {previewCards.map((card, idx) => (
                    <Card key={idx} className="p-3">
                      <div className="font-medium">{card.title}</div>
                      {card.body && (
                        <p className="text-sm text-muted-foreground mt-1">{card.body}</p>
                      )}
                      {Object.keys(card.fields || {}).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {Object.entries(card.fields).map(([key, value]) => (
                            <Badge key={key} variant="outline" className="text-xs">
                              {key}: {value}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setPreviewDeck(null); setPreviewCards([]); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={importing || loadingPreview || previewCards.length === 0 || loadFailed}
              className="gap-2"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FolderPlus className="h-4 w-4" />
              )}
              Add to {currentWorkspace?.name || 'Workspace'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LibraryPage;
